/**
 * OAuthConsentPage
 * =================
 * Frontend consent screen for OAuth 2.0 Authorization Code flow (BR-006).
 *
 * Triggered when an external MCP client (e.g. Claude.ai) requests access to
 * the user's Xpert account. The backend's GET /oauth/authorize validates the
 * request, then 302-redirects here with all the OAuth params in the URL.
 *
 * Flow:
 *  1. Read params from URL (client_id, redirect_uri, scope, state, PKCE, etc.)
 *  2. If user not logged in → redirect to /login?return_to=<current URL>
 *  3. GET /api/oauth/consent-info — fetch the client_name + per-scope details
 *  4. Render consent UI (Clean Enterprise palette)
 *  5. On Allow → POST /api/oauth/authorize/confirm with all params + decision
 *  6. Server returns {redirect_url} → window.location.href = redirect_url
 *     (takes user back to e.g. claude.ai/api/mcp/auth_callback?code=...)
 *  7. On Deny → same as Allow, server returns an error-tagged redirect_url
 *
 * Why this lives in the frontend (not server-rendered HTML):
 * --------------------------------------------------------
 * The user's JWT is stored in localStorage by the AuthProvider in App.js.
 * Browsers do NOT include localStorage values automatically with direct-
 * navigation requests, so a server-rendered consent page can't see the
 * logged-in user. The React app does have access to localStorage and to
 * the axios-default Authorization header, so consent works correctly here.
 *
 * Route: /oauth/consent  (NOT inside ProtectedRoute — we handle the
 * unauthenticated redirect ourselves to preserve the return_to URL)
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Shield, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import CheckELogo from "@/components/CheckELogo";

export default function OAuthConsentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  // Pull the OAuth params straight from the URL — these came from the
  // backend's 302 redirect after it validated them.
  const oauthParams = {
    response_type:          searchParams.get("response_type") || "code",
    client_id:              searchParams.get("client_id") || "",
    redirect_uri:           searchParams.get("redirect_uri") || "",
    scope:                  searchParams.get("scope") || "",
    state:                  searchParams.get("state") || "",
    code_challenge:         searchParams.get("code_challenge") || "",
    code_challenge_method:  searchParams.get("code_challenge_method") || "S256",
  };

  // Local state
  const [info, setInfo]       = useState(null);   // {client_name, scopes, redirect_host, ...}
  const [loadErr, setLoadErr] = useState("");
  const [decision, setDecision] = useState(null); // "allow" | "deny" | null
  const didRedirectToLoginRef = useRef(false);
  const didProcessFragmentRef = useRef(false);

  // ── Step 1: process a token in the URL fragment if Google OAuth just landed
  // us here. The backend Google-callback builds the post-auth URL as:
  //   https://xpert.company/oauth/consent?...#token=<jwt>&is_expert=<bool>
  // AuthCallback would normally process this, but since the path is
  // /oauth/consent (not /auth/callback) it never runs. We do the work inline.
  useEffect(() => {
    if (didProcessFragmentRef.current) return;
    const hash = window.location.hash;
    const m = hash.match(/token=([^&]+)/);
    if (!m) return;

    didProcessFragmentRef.current = true;
    const token = m[1];
    localStorage.setItem("xpert_token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    // Strip the fragment so a refresh doesn't re-process it (and so the
    // token doesn't sit in window.location.hash visible to any extension).
    window.history.replaceState(null, "",
      window.location.pathname + window.location.search);

    // Tell the AuthProvider to re-fetch /api/auth/me with the new token.
    // We don't import fetchUser via destructuring at the top so we can
    // grab it conditionally — the page-level useAuth already returns it.
    // A simple full reload also works and is more reliable across timing
    // edge cases (auth provider initial useEffect vs. our fragment handling).
    window.location.reload();
  }, []);

  // ── Step 2: auth gate — if no token in fragment AND no user, → /login
  useEffect(() => {
    if (authLoading) return;
    // If we just saved a fragment token, a reload is in flight — don't redirect.
    if (didProcessFragmentRef.current) return;
    if (!user && !didRedirectToLoginRef.current) {
      didRedirectToLoginRef.current = true;
      const full = window.location.pathname + window.location.search;
      navigate(`/login?return_to=${encodeURIComponent(full)}`, { replace: true });
    }
  }, [user, authLoading, navigate]);

  // ── Fetch consent-info from the backend ────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    if (!oauthParams.client_id || !oauthParams.redirect_uri) {
      setLoadErr("Missing client_id or redirect_uri in URL — this consent link is malformed.");
      return;
    }

    const params = new URLSearchParams({
      client_id:    oauthParams.client_id,
      redirect_uri: oauthParams.redirect_uri,
    });
    if (oauthParams.scope) params.set("scope", oauthParams.scope);

    axios.get(`${API}/oauth/consent-info?${params.toString()}`)
      .then((res) => setInfo(res.data))
      .catch((err) => {
        const msg = err?.response?.data?.detail || err.message || "Failed to load consent info.";
        setLoadErr(msg);
      });
  }, [user, oauthParams.client_id, oauthParams.redirect_uri, oauthParams.scope]);

  // ── Submit allow/deny — backend returns the redirect_url for window.location ─
  const handleDecision = async (choice) => {
    setDecision(choice);
    try {
      const res = await axios.post(`${API}/oauth/authorize/confirm`, {
        ...oauthParams,
        decision: choice,
      });
      const url = res?.data?.redirect_url;
      if (!url) throw new Error("Server did not return a redirect_url.");
      // Hard navigation so we leave xpert.com and arrive at the client's callback.
      window.location.href = url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Authorization failed.";
      toast.error(msg);
      setDecision(null);
    }
  };

  // ── Loading / error states ─────────────────────────────────────────────────
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-slateline-400 animate-spin" />
      </div>
    );
  }

  if (loadErr) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slateline-200 rounded-xl shadow-sm p-7">
          <div className="flex items-center gap-2 text-rose-600 mb-3">
            <AlertTriangle className="w-5 h-5" />
            <h2 className="font-display text-lg">Can't complete authorization</h2>
          </div>
          <p className="text-sm text-slateline-600 leading-relaxed">{loadErr}</p>
          <button
            onClick={() => navigate("/")}
            className="mt-5 text-sm text-brand-green hover:underline"
          >
            ← Back to Xpert
          </button>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-slateline-400 animate-spin" />
      </div>
    );
  }

  // ── Consent UI ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-canvas text-ink font-body flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-md bg-white border border-slateline-200 rounded-2xl shadow-md p-7 sm:p-8">
        {/* Brand mark */}
        <div className="flex items-center justify-center mb-6">
          <CheckELogo className="w-12 h-12" />
        </div>

        <h1 className="font-display text-2xl sm:text-3xl text-ink text-center leading-tight">
          Authorize{" "}
          <span className="text-brand-green">{info.client_name}</span>?
        </h1>

        <p className="text-sm text-slateline-600 text-center mt-3 leading-relaxed">
          <strong>{info.client_name}</strong> is requesting access to your{" "}
          <span className="italic text-brand-green font-semibold">xpert</span> account.
          On approval, it will be able to:
        </p>

        {/* Scope list */}
        <div className="mt-5 bg-slateline-50 border border-slateline-100 rounded-lg p-4">
          <ul className="space-y-2">
            {info.scopes.map((s) => (
              <li key={s.key} className="flex items-start gap-2 text-sm text-ink">
                <Shield className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" />
                <span>{s.description}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Where this is going */}
        {info.redirect_host && (
          <p className="text-xs text-slateline-500 text-center mt-4 flex items-center justify-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" />
            After approval you'll be redirected to <strong>{info.redirect_host}</strong>
          </p>
        )}

        {/* Buttons */}
        <div className="flex gap-3 mt-7">
          <button
            type="button"
            disabled={decision !== null}
            onClick={() => handleDecision("deny")}
            className="flex-1 h-11 rounded-lg border border-slateline-200 bg-white
                       text-slateline-600 font-semibold text-sm
                       hover:bg-slateline-50 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {decision === "deny" ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : "Cancel"}
          </button>
          <button
            type="button"
            disabled={decision !== null}
            onClick={() => handleDecision("allow")}
            className="flex-1 h-11 rounded-lg bg-brand-green hover:bg-brand-greenHover
                       text-white font-semibold text-sm transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {decision === "allow" ? (
              <Loader2 className="w-4 h-4 mx-auto animate-spin" />
            ) : (
              "Allow access"
            )}
          </button>
        </div>

        {/* Signed-in indicator */}
        <p className="text-xs text-slateline-500 text-center mt-6 pt-5 border-t border-slateline-100">
          Signed in as <strong className="text-ink">{user.email}</strong>
        </p>
      </div>
    </div>
  );
}
